import { useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Copy, Eye, EyeOff, Settings2, X } from "lucide-react";

const baseFields = [
  { key: "numero", label: "Ramal", mono: true, placeholder: "1001" },
  {
    key: "batch_enabled",
    label: "Cadastrar faixa de ramais",
    type: "toggle",
    full: true,
    hint: "Ative para criar varios ramais de uma vez usando o numero inicial e final.",
  },
  {
    key: "numero_fim",
    label: "Ramal final da faixa",
    mono: true,
    hidden: (form) => !form.batch_enabled || form.__editing,
    placeholder: "1010",
    hint: "Se informar 1001 ate 1010, o sistema cria todos os ramais nesse intervalo.",
  },
  { key: "nome", label: "Nome" },
  { key: "senha", label: "Senha SIP", type: "password", mono: true, placeholder: "Automatica: codigo do cliente" },
  { key: "caller_id_interno", label: "Caller ID interno", mono: true, placeholder: "Ex.: 201" },
  { key: "caller_id_externo", label: "Caller ID externo", mono: true, placeholder: "Ex.: 557135999160" },
  {
    key: "outbound_pin_required",
    label: "Exigir PIN para ligacao externa",
    type: "toggle",
    full: true,
    hint: "Quando ativado, este ramal so completa chamadas externas apos validar um PIN de saida cadastrado neste cliente.",
  },
  {
    key: "recording_mode",
    label: "Gravacao de chamadas",
    type: "select",
    full: true,
    options: [
      { value: "off", label: "Nao gravar" },
      { value: "all", label: "Todas as ligacoes" },
      { value: "internal", label: "Somente ligacoes internas" },
      { value: "external", label: "Somente ligacoes externas" },
    ],
    hint: "As gravacoes seguem a configuracao deste ramal e ficam disponiveis no relatorio de gravacoes.",
  },
  {
    key: "codec",
    label: "Codecs",
    type: "checkbox-list",
    mono: true,
    full: true,
    options: [
      { value: "AUTO", label: "AUTO", hint: "Negociacao automatica do sistema" },
      { value: "PCMA", label: "PCMA" },
      { value: "PCMU", label: "PCMU" },
      { value: "G722", label: "G722" },
      { value: "G729", label: "G729" },
      { value: "OPUS", label: "OPUS" },
    ],
    hint: "Selecione AUTO para enquadramento automatico ou marque codecs especificos para fixar manualmente.",
  },
  { key: "transporte", label: "Conexao SIP", type: "select", options: [
    { value: "udp", label: "UDP - porta 5060" },
    { value: "tcp", label: "TCP - porta 5060" },
    { value: "tls", label: "TLS seguro - porta 5061" },
  ] },
  { key: "enabled", label: "Ativo", type: "toggle" },
];

export default function Ramais() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [permissionClasses, setPermissionClasses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [ciscoSettingsByTenant, setCiscoSettingsByTenant] = useState({});
  const [setupInfo, setSetupInfo] = useState(null);
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
    Promise.all([
      api.get("/call-permissions", unscoped),
      api.get("/rotas", unscoped),
      api.get("/tenants", isSuperAdmin ? { skipTenantScope: true } : undefined),
    ])
      .then(([permissionsResponse, routesResponse, tenantsResponse]) => {
        setPermissionClasses(permissionsResponse.data || []);
        setRoutes(routesResponse.data || []);
        setTenants(tenantsResponse.data || []);
      })
      .catch(() => {
        setPermissionClasses([]);
        setRoutes([]);
        setTenants([]);
      });
  }, [isSuperAdmin]);

  const tenantMap = useMemo(
    () => Object.fromEntries((tenants || []).map((tenant) => [tenant.id, tenant.nome])),
    [tenants],
  );
  const permissionMap = useMemo(
    () => Object.fromEntries((permissionClasses || []).map((item) => [item.id, item])),
    [permissionClasses],
  );
  const tenantOptions = useMemo(
    () => (tenants || []).map((tenant) => ({ value: tenant.id, label: tenant.nome })),
    [tenants],
  );
  const tenantFor = (tenantId) => (
    tenants.find((tenant) => tenant.id === (tenantId || user?.tenant_id || "")) || null
  );
  const sipConfigFor = (ramal) => {
    const tenant = tenantFor(ramal?.tenant_id);
    const branch = (tenant?.branches || []).find((item) => item.id === ramal?.branch_id);
    const prefix = String(branch?.sip_auth_prefix || tenant?.sip_auth_prefix || "").trim();
    const usePrefix = tenant?.use_prefixed_sip_auth !== false;
    const number = String(ramal?.numero || "").trim();
    const transport = String(ramal?.transporte || "udp").toUpperCase();
    return {
      extension: number,
      displayName: String(ramal?.nome || number).trim(),
      username: String(ramal?.auth_usuario || (usePrefix ? `${prefix}${number}` : number)).trim(),
      password: String(ramal?.senha || "").trim(),
      domain: String(ramal?.sip_domain || (tenant?.domains || [])[0] || "sipmbr.voxyra.net.br").trim(),
      server: String(ramal?.sip_server || "sipmbr.voxyra.net.br").trim(),
      port: Number(ramal?.sip_port || (transport === "TLS" ? 5061 : 5060)),
      transport,
      codecs: Array.isArray(ramal?.codec) ? ramal.codec.join(",") : String(ramal?.codec || "AUTO").trim(),
    };
  };
  const copySetup = async (label, value) => {
    await navigator.clipboard.writeText(String(value || ""));
    setCopied(label);
    setTimeout(() => setCopied(""), 1400);
  };
  const openSetup = (ramal) => {
    setShowSetupPassword(false);
    setCopied("");
    setSetupInfo({ type: "ramal", ramal });
  };
  const branchOptionsFor = (tenantId) => {
    const tenant = tenantFor(tenantId);
    const branches = Array.isArray(tenant?.branches) ? tenant.branches : [];
    return [
      { value: "", label: "Cliente principal" },
      ...branches
        .filter((branch) => branch?.enabled !== false)
        .map((branch) => ({
          value: branch.id,
          label: branch.nome || branch.id,
        })),
    ];
  };
  const branchNameFor = (tenantId, branchId) => {
    if (!branchId) return "Cliente principal";
    const tenant = tenantFor(tenantId);
    const branch = (tenant?.branches || []).find((item) => item.id === branchId);
    return branch?.nome || branchId;
  };
  const ciscoTemplateFor = (tenantId) => (
    ciscoSettingsByTenant[String(tenantId || "").trim()] || null
  );
  const applyCiscoTemplateToForm = (current) => {
    const next = { ...current };
    if (String(next.device_brand || "").trim().toLowerCase() !== "cisco") return next;
    const settings = ciscoTemplateFor(next.tenant_id || user?.tenant_id || "");
    const protocol = String(settings?.protocolo || "").trim().toUpperCase();
    if (["UDP", "TCP", "TLS"].includes(protocol)) {
      next.transporte = protocol.toLowerCase();
    }
    if (!String(next.device_model || "").trim()) {
      next.device_model = "6921";
    }
    return next;
  };
  const ciscoTemplateHint = (form) => {
    if (String(form?.device_brand || "").trim().toLowerCase() !== "cisco") {
      return "Se selecionar uma marca, o sistema cria e atualiza o provisionamento automaticamente ao salvar o ramal.";
    }
    const tenantId = form?.tenant_id || user?.tenant_id || "";
    const settings = ciscoTemplateFor(tenantId);
    const tenant = tenantFor(tenantId);
    const fallbackDomain = String((tenant?.domains || [])[0] || "sipmbr.voxyra.net.br").trim();
    const servidor = String(settings?.servidor || "sipmbr.voxyra.net.br").trim();
    const dominio = String(settings?.dominio_dns || fallbackDomain).trim();
    const protocolo = String(settings?.protocolo || "UDP").trim().toUpperCase();
    const porta = Number(settings?.porta || (protocolo === "TLS" ? 5061 : 5060));
    const registro = Number(settings?.register_expires || 60);
    const antecedencia = Number(settings?.register_delta || 15);
    return `Template Cisco do cliente: servidor ${servidor} | dominio ${dominio} | porta ${porta} | protocolo ${protocolo} | registro ${registro}s | antecedencia ${antecedencia}s.`;
  };
  const normalizeBranchValue = (tenantId, branchValue) => {
    const rawValue = String(branchValue || "").trim();
    if (!rawValue) return "";
    const options = branchOptionsFor(tenantId);
    const direct = options.find((option) => option.value === rawValue);
    if (direct) return direct.value;
    const lowered = rawValue.toLowerCase();
    const slugify = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9_.-]+/g, "-").replace(/^-+|-+$/g, "");
    const match = options.find((option) => {
      const label = String(option.label || "").trim();
      return (
        label.toLowerCase() === lowered ||
        slugify(label) === slugify(rawValue) ||
        String(option.value || "").toLowerCase() === lowered
      );
    });
    return match?.value || rawValue;
  };
  const normalizeRamalForm = (current, row = null) => {
    const effectiveTenantId = current.tenant_id || row?.tenant_id || user?.tenant_id || "";
    const normalizedBranchId = normalizeBranchValue(effectiveTenantId, current.branch_id || row?.branch_id || "");
    const legacyCallerId = current.caller_id || row?.caller_id || "";
    const next = {
      ...current,
      tenant_id: effectiveTenantId,
      branch_id: normalizedBranchId,
      caller_id_interno: current.caller_id_interno ?? row?.caller_id_interno ?? legacyCallerId,
      caller_id_externo: current.caller_id_externo ?? row?.caller_id_externo ?? legacyCallerId,
    };
    const options = branchOptionsFor(effectiveTenantId);
    if (!options.some((option) => option.value === next.branch_id)) {
      next.branch_id = "";
    }
    const allowedPermissionIds = permissionClasses
      .filter((item) => item.enabled !== false && item.tenant_id === effectiveTenantId)
      .map((item) => item.id);
    if (next.permission_class_id && !allowedPermissionIds.includes(next.permission_class_id)) {
      next.permission_class_id = "";
    }
    const allowedRouteIds = routes
      .filter((route) => route.enabled !== false && route.tenant_id === effectiveTenantId)
      .map((route) => route.id);
    if (next.follow_me_route_id && !allowedRouteIds.includes(next.follow_me_route_id)) {
      next.follow_me_route_id = "";
    }
    return next;
  };

  useEffect(() => {
    const tenantIds = (
      isSuperAdmin
        ? (tenants || []).map((tenant) => tenant.id).filter(Boolean)
        : [user?.tenant_id].filter(Boolean)
    );
    if (!tenantIds.length) {
      setCiscoSettingsByTenant({});
      return;
    }
    let cancelled = false;
    Promise.all(
      tenantIds.map(async (tenantId) => {
        try {
          const query = isSuperAdmin ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
          const response = await api.get(`/provisioning/cisco/settings${query}`, isSuperAdmin ? { skipTenantScope: true } : undefined);
          return [tenantId, response.data || null];
        } catch (_) {
          return [tenantId, null];
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setCiscoSettingsByTenant(Object.fromEntries(entries.filter(([, value]) => !!value)));
    });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, tenants, user?.tenant_id]);
  const validateRamalForm = (form) => {
    const tenantId = String(form.tenant_id || user?.tenant_id || "").trim();
    if (isSuperAdmin && !tenantId) throw new Error("Selecione o cliente do ramal.");

    const numero = String(form.numero || "").trim();
    if (!numero) throw new Error("Informe o numero do ramal.");
    if (!/^\d{2,10}$/.test(numero)) throw new Error("O ramal deve conter somente numeros, entre 2 e 10 digitos.");

    if (!form.__editing && form.batch_enabled) {
      const numeroFim = String(form.numero_fim || "").trim();
      if (!numeroFim) throw new Error("Informe o ramal final da faixa.");
      if (!/^\d{2,10}$/.test(numeroFim)) throw new Error("O ramal final da faixa deve conter somente numeros.");
      if (Number(numeroFim) < Number(numero)) throw new Error("O ramal final deve ser maior ou igual ao inicial.");
    }

    const callerIdInterno = String(form.caller_id_interno || "").trim();
    if (callerIdInterno && !/^\+?[0-9]{2,20}$/.test(callerIdInterno)) {
      throw new Error("O Caller ID interno deve conter somente numeros, com + opcional no inicio.");
    }

    const callerIdExterno = String(form.caller_id_externo || "").trim();
    if (callerIdExterno && !/^\+?[0-9]{3,20}$/.test(callerIdExterno)) {
      throw new Error("O Caller ID externo deve conter somente numeros, com + opcional no inicio.");
    }

    const branchId = String(form.branch_id || "").trim();
    if (branchId) {
      const allowedBranchIds = branchOptionsFor(tenantId).map((option) => option.value).filter(Boolean);
      if (!allowedBranchIds.includes(branchId)) {
        throw new Error("A unidade/filial selecionada nao pertence ao cliente informado.");
      }
    }

    const deviceBrand = String(form.device_brand || "none").trim().toLowerCase();
    if (deviceBrand !== "none") {
      const deviceMac = String(form.device_mac || "").trim();
      if (deviceMac && !/^[0-9A-Fa-f:\-\.]{12,20}$/.test(deviceMac)) {
        throw new Error("O MAC do aparelho esta em formato invalido.");
      }
    }
  };

  const fields = useMemo(() => [
    ...baseFields.slice(0, 4),
    ...(isSuperAdmin ? [{
      key: "tenant_id",
      label: "Cliente",
      type: "select",
      full: true,
      options: () => [{ value: "", label: "Selecione o cliente" }, ...tenantOptions],
      onChange: (next, value) => applyCiscoTemplateToForm(
        normalizeRamalForm({
          ...next,
          tenant_id: value,
          branch_id: "",
          permission_class_id: "",
          follow_me_route_id: "",
        }),
      ),
    }] : []),
    {
      key: "branch_id",
      label: "Unidade / filial",
      type: "select",
      full: true,
      options: (form) => branchOptionsFor(form.tenant_id),
      onChange: (next, value) => ({ ...next, branch_id: value }),
      hint: "O prefixo SIP e as configuracoes da filial serao aplicados quando houver uma unidade selecionada.",
    },
    {
      key: "permission_class_id",
      label: "Classe de permissao de saida",
      type: "select",
      full: true,
      options: (form) => {
        const tenantId = form.tenant_id || user?.tenant_id;
        return [
          { value: "", label: "Sem restricao - comportamento atual" },
          ...permissionClasses
            .filter((item) => item.enabled !== false && (!tenantId || item.tenant_id === tenantId))
            .map((item) => ({ value: item.id, label: item.nome })),
        ];
      },
      hint: "A classe define chamadas entre ramais, local, DDD, DDI, bloqueios e a rota permitida.",
    },
    {
      key: "voicemail_enabled",
      label: "Correio de voz",
      type: "toggle",
      full: true,
      onChange: (next, checked) => ({
        ...next,
        voicemail_pin: checked && !next.voicemail_pin ? String(next.numero || "") : next.voicemail_pin,
      }),
      hint: "Se o ramal e o Siga-me nao atenderem, a chamada sera enviada para esta caixa postal.",
    },
    {
      key: "voicemail_pin",
      label: "PIN do correio de voz",
      mono: true,
      hidden: (form) => !form.voicemail_enabled,
      placeholder: "4 a 10 digitos",
    },
    {
      key: "voicemail_no_answer_seconds",
      label: "Tempo para atender (segundos)",
      type: "number",
      hidden: (form) => !form.voicemail_enabled,
      hint: "Entre 5 e 120 segundos antes de gravar a mensagem.",
    },
    {
      key: "voicemail_email",
      label: "Enviar mensagens por e-mail",
      type: "email",
      full: true,
      hidden: (form) => !form.voicemail_enabled,
      placeholder: "usuario@empresa.com.br",
      hint: "Opcional. A mensagem permanece disponivel no painel, salvo se a exclusao apos envio estiver ativa.",
    },
    {
      key: "voicemail_attach_email",
      label: "Anexar audio ao e-mail",
      type: "toggle",
      hidden: (form) => !form.voicemail_enabled || !form.voicemail_email,
    },
    {
      key: "voicemail_delete_after_email",
      label: "Excluir do servidor apos enviar",
      type: "toggle",
      hidden: (form) => !form.voicemail_enabled || !form.voicemail_email,
    },
    {
      key: "voicemail_ask_pin",
      label: "Pedir PIN ao consultar pelo *97",
      type: "toggle",
      hidden: (form) => !form.voicemail_enabled,
    },
    {
      key: "voicemail_skip_instructions",
      label: "Pular instrucoes ao deixar mensagem",
      type: "toggle",
      hidden: (form) => !form.voicemail_enabled,
    },
    {
      key: "voicemail_operator_destination",
      label: "Destino ao solicitar operador",
      mono: true,
      hidden: (form) => !form.voicemail_enabled,
      placeholder: "Ramal, grupo ou fila (opcional)",
    },
    {
      key: "busy_announcement_enabled",
      label: "Avisar quando ocupado",
      type: "toggle",
      full: true,
      hint: "Quando este ramal ja estiver em chamada, o sistema informa ao chamador que ele esta ocupado antes de encerrar.",
    },
    {
      key: "call_waiting_enabled",
      label: "Permitir chamada em espera",
      type: "toggle",
      full: true,
      hint: "Quando desativado, uma segunda chamada para este ramal ouve o aviso de ocupado e e encerrada.",
    },
    {
      key: "callcenter_exclusive_when_logged",
      label: "Uso exclusivo do Call Center",
      type: "toggle",
      full: true,
      hint: "Enquanto um agente estiver logado neste ramal, chamadas internas diretas sao recusadas. A fila continua entregando chamadas normalmente.",
    },
    { key: "follow_me_enabled", label: "Siga-me", type: "toggle", full: true },
    {
      key: "follow_me_delay_seconds",
      label: "Tempo antes do desvio (segundos)",
      type: "number",
      hidden: (form) => !form.follow_me_enabled,
      hint: "De 1 a 300 segundos. O ramal toca durante este tempo antes de desviar.",
    },
    {
      key: "follow_me_destination_type",
      label: "Tipo de destino",
      type: "select",
      hidden: (form) => !form.follow_me_enabled,
      options: [
        { value: "ramal", label: "Ramal" },
        { value: "grupo", label: "Grupo" },
        { value: "fila", label: "Fila" },
        { value: "externo", label: "Numero externo" },
      ],
      onChange: (next) => ({ ...next, follow_me_destination: "", follow_me_route_id: "" }),
    },
    {
      key: "follow_me_destination",
      label: "Destino do Siga-me",
      mono: true,
      hidden: (form) => !form.follow_me_enabled,
      placeholder: "Ramal, grupo, fila ou numero externo",
      hint: "Informe somente os digitos exatamente como devem ser discados.",
    },
    {
      key: "follow_me_route_id",
      label: "Rota para o numero externo",
      type: "select",
      hidden: (form) => !form.follow_me_enabled || form.follow_me_destination_type !== "externo",
      options: (form) => {
        const tenantId = form.tenant_id || user?.tenant_id;
        return [
          { value: "", label: "-- selecione a rota --" },
          ...routes
            .filter((route) => route.enabled !== false && (!tenantId || route.tenant_id === tenantId))
            .map((route) => ({ value: route.id, label: route.nome })),
        ];
      },
      hint: "O desvio externo sera enviado somente pela rota selecionada.",
    },
    {
      key: "device_brand",
      label: "Provisionamento automatico",
      type: "select",
      full: true,
      options: [
        { value: "none", label: "Nenhum aparelho gerenciado" },
        { value: "cisco", label: "Cisco" },
        { value: "yealink", label: "Yealink" },
      ],
      onChange: (next, value) => applyCiscoTemplateToForm({
        ...next,
        device_brand: value,
        device_model: value === "cisco" ? "6921" : value === "yealink" ? "T31P" : "",
        device_mac: value === "none" ? "" : next.device_mac,
      }),
      hint: ciscoTemplateHint,
    },
    {
      key: "device_model",
      label: "Modelo do aparelho",
      type: "select",
      hidden: (form) => !form.device_brand || form.device_brand === "none",
      options: (form) => (
        form.device_brand === "cisco"
          ? [{ value: "6921", label: "Cisco 6921" }, { value: "6941", label: "Cisco 6941" }]
          : [{ value: "T31P", label: "Yealink T31P" }, { value: "T33G", label: "Yealink T33G" }, { value: "T46U", label: "Yealink T46U" }, { value: "T54W", label: "Yealink T54W" }]
      ),
    },
    {
      key: "device_mac",
      label: "MAC do aparelho",
      mono: true,
      hidden: (form) => !form.device_brand || form.device_brand === "none",
      placeholder: "001122AABBCC",
      hint: "Opcional no cadastro em lote. Voce pode editar depois no menu de provisionamento e informar o MAC real do aparelho.",
    },
    ...baseFields.slice(4),
  ], [branchOptionsFor, isSuperAdmin, permissionClasses, routes, tenantOptions, user?.tenant_id]);

  return (
    <>
    <CrudPage
      title="Ramais"
      subtitle="IPBX interno"
      endpoint="/ramais"
      testidPrefix="ramais"
      fields={fields}
      selectableRows
      bulkDeleteLabel="Apagar selecionados"
      transformFormOpen={(form, row) => normalizeRamalForm({
        ...form,
        recording_mode: row?.recording_mode || (row?.recording_enabled ? "all" : (form.recording_mode || "off")),
        codec: (() => {
          const raw = row?.codec ?? form.codec ?? "AUTO";
          if (Array.isArray(raw)) return raw.length ? raw : ["AUTO"];
          const items = String(raw || "AUTO")
            .split(",")
            .map((item) => item.trim().toUpperCase())
            .filter(Boolean);
          return items.length ? items : ["AUTO"];
        })(),
        __editing: !!row,
        batch_enabled: false,
        numero_fim: "",
      }, row)}
      transformSubmit={(form) => {
        const normalized = normalizeRamalForm({
          ...form,
          tenant_id: isSuperAdmin ? (form.tenant_id || user?.tenant_id || "") : (user?.tenant_id || form.tenant_id || ""),
        });
        validateRamalForm(normalized);
        return ({
        ...normalized,
        recording_mode: form.recording_mode || (form.recording_enabled ? "all" : "off"),
        recording_enabled: (form.recording_mode || (form.recording_enabled ? "all" : "off")) !== "off",
        codec: (() => {
          const selected = Array.isArray(form.codec)
            ? form.codec.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)
            : String(form.codec || "AUTO").split(",").map((item) => item.trim().toUpperCase()).filter(Boolean);
          if (!selected.length || selected.includes("AUTO")) return "AUTO";
          return [...new Set(selected)].join(",");
        })(),
        batch_enabled: !form.__editing && !!form.batch_enabled,
        numero_fim: !form.__editing && form.batch_enabled ? (form.numero_fim || null) : null,
        caller_id: undefined,
        voicemail_email: form.voicemail_email || null,
        voicemail_operator_destination: form.voicemail_operator_destination || null,
        voicemail_attach_email: !!form.voicemail_email && !!form.voicemail_attach_email,
        voicemail_delete_after_email: !!form.voicemail_email && !!form.voicemail_delete_after_email,
        device_brand: form.device_brand || "none",
        device_model: form.device_brand && form.device_brand !== "none" ? form.device_model : null,
        device_mac: form.device_brand && form.device_brand !== "none" ? form.device_mac : null,
        __editing: undefined,
      })}}
      onSaved={(response, context) => {
        if (context.editing) return;
        if (response?.batch_created) {
          setSetupInfo({
            type: "batch",
            count: response.batch_created,
            start: response.range_start,
            end: response.range_end,
          });
          return;
        }
        const created = (context.rows || []).find((row) => (
          row.id === response?.id
          || (
            String(row.numero) === String(context.payload?.numero)
            && String(row.tenant_id || "") === String(context.payload?.tenant_id || "")
          )
        ));
        openSetup(created || { ...context.payload, ...response });
      }}
      renderActions={(ramal) => (
        <button type="button" className="sbc-btn" onClick={() => openSetup(ramal)} title="Ver configuracao SIP" data-testid={`ramais-config-${ramal.id}`}>
          <Settings2 size={12} />
        </button>
      )}
      headers={[...(isSuperAdmin ? ["Cliente"] : []), "Unidade / Filial", "Ramal", "Nome", "Conexao", "Dominio SIP", "CID interno", "CID externo", "PIN externo", "Permissao de saida", "Correio de voz", "Siga-me", "Codecs", "Status"]}
      defaultValues={{ tenant_id: user?.tenant_id || "", branch_id: "", numero: "", batch_enabled: false, numero_fim: "", __editing: false, nome: "", senha: "", voicemail_enabled: false, voicemail_pin: "", voicemail_email: "", voicemail_attach_email: false, voicemail_delete_after_email: false, voicemail_ask_pin: true, voicemail_skip_instructions: false, voicemail_no_answer_seconds: 25, voicemail_operator_destination: "", busy_announcement_enabled: false, call_waiting_enabled: true, callcenter_exclusive_when_logged: false, caller_id_interno: "", caller_id_externo: "", outbound_pin_required: false, permission_class_id: "", follow_me_enabled: false, follow_me_delay_seconds: 20, follow_me_destination_type: "ramal", follow_me_destination: "", follow_me_route_id: "", device_brand: "none", device_model: "", device_mac: "", recording_enabled: false, recording_mode: "off", codec: ["AUTO"], transporte: "udp", enabled: true }}
      renderRow={(ramal) => <>
        {isSuperAdmin && <td>{tenantMap[ramal.tenant_id] || ramal.tenant_id || "-"}</td>}
        <td>{branchNameFor(ramal.tenant_id, ramal.branch_id)}</td>
        <td className="font-mono">{ramal.numero}</td>
        <td>{ramal.nome}</td>
        <td><span className={`sbc-tag ${ramal.transporte === "tls" ? "tag-online" : ""}`}>{String(ramal.transporte || "udp").toUpperCase()} : {ramal.sip_port || 5060}</span></td>
        <td><div className="font-mono text-xs">{ramal.sip_domain || "-"}</div><div className="font-mono text-[10px] text-[color:var(--text-muted)]">Proxy: {ramal.sip_server || "sipmbr.voxyra.net.br"}</div></td>
        <td>{ramal.caller_id_interno || ramal.numero || "-"}</td>
        <td>{ramal.caller_id_externo || ramal.caller_id || "-"}</td>
        <td>{ramal.outbound_pin_required ? <span className="sbc-tag tag-warning">Exigido</span> : <span className="text-xs text-[color:var(--text-muted)]">Nao</span>}</td>
        <td>{ramal.permission_class_id ? <span className="sbc-tag tag-online">{permissionMap[ramal.permission_class_id]?.nome || "Classe vinculada"}</span> : <span className="text-xs text-[color:var(--text-muted)]">Sem restricao</span>}</td>
        <td>{ramal.voicemail_enabled ? <div><span className="sbc-tag tag-online">Ativo</span><div className="mt-1 font-mono text-[10px] text-[color:var(--text-muted)]">{ramal.voicemail_no_answer_seconds || 25}s</div></div> : <span className="text-xs text-[color:var(--text-muted)]">Inativo</span>}</td>
        <td>{ramal.follow_me_enabled ? <div><span className="sbc-tag tag-online">Ativo</span><div className="mt-1 font-mono text-[10px] text-[color:var(--text-muted)]">{ramal.follow_me_delay_seconds || 20}s -&gt; {ramal.follow_me_destination}</div></div> : <span className="text-xs text-[color:var(--text-muted)]">Inativo</span>}</td>
        <td className="font-mono text-xs">{ramal.codec}</td>
        <td>{ramal.enabled ? "Ativo" : "Inativo"}</td>
      </>}
    />
    {setupInfo && (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-4" onClick={() => setSetupInfo(null)}>
        <div className="min-h-full flex items-center justify-center">
          <div className="sbc-card w-full max-w-2xl p-5 md:p-6" onClick={(event) => event.stopPropagation()} data-testid="ramais-sip-config-modal">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Configuracao do aparelho</div>
                <h2 className="text-xl font-semibold">{setupInfo.type === "batch" ? "Ramais criados com sucesso" : `Ramal ${setupInfo.ramal?.numero}`}</h2>
                <p className="text-sm text-[color:var(--text-secondary)] mt-1">{setupInfo.type === "batch" ? "A faixa foi cadastrada. Abra o botao de configuracao de cada ramal para consultar suas credenciais." : "Use estes dados em um softphone, telefone IP ou aplicativo SIP."}</p>
              </div>
              <button type="button" className="sbc-btn" onClick={() => setSetupInfo(null)} title="Fechar"><X size={14}/></button>
            </div>
            {setupInfo.type === "batch" ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Quantidade</div><div className="text-2xl font-mono mt-2">{setupInfo.count}</div></div>
                <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Primeiro ramal</div><div className="text-2xl font-mono mt-2">{setupInfo.start}</div></div>
                <div className="sbc-card p-4"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Ultimo ramal</div><div className="text-2xl font-mono mt-2">{setupInfo.end}</div></div>
              </div>
            ) : (() => {
              const config = sipConfigFor(setupInfo.ramal);
              const rows = [
                ["Ramal", config.extension],
                ["Nome de exibicao", config.displayName],
                ["Usuario SIP / Auth ID", config.username],
                ["Senha SIP", config.password],
                ["Dominio / Realm", config.domain],
                ["Servidor / Proxy", config.server],
                ["Porta", config.port],
                ["Transporte", config.transport],
                ["Codecs", config.codecs],
              ];
              return <>
                <div className="overflow-hidden border border-[color:var(--border-default)] rounded-sm">
                  {rows.map(([label, value]) => {
                    const password = label === "Senha SIP";
                    const visibleValue = password && !showSetupPassword ? "••••••••••••" : value;
                    return <div key={label} className="grid grid-cols-[minmax(120px,190px)_1fr_auto] items-center gap-3 px-3 py-2.5 border-b last:border-b-0 border-[color:var(--border-default)]">
                      <div className="text-[10px] uppercase text-[color:var(--text-muted)]">{label}</div>
                      <div className="font-mono text-sm break-all">{visibleValue || "-"}</div>
                      <div className="flex gap-1">
                        {password && <button type="button" className="sbc-btn" onClick={() => setShowSetupPassword((current) => !current)} title={showSetupPassword ? "Ocultar senha" : "Mostrar senha"}>{showSetupPassword ? <EyeOff size={12}/> : <Eye size={12}/>}</button>}
                        <button type="button" className="sbc-btn" onClick={() => copySetup(label, value)} title={`Copiar ${label}`}><Copy size={12}/></button>
                      </div>
                    </div>;
                  })}
                </div>
                {copied && <div className="mt-2 text-xs text-[color:var(--accent-green)]">{copied} copiado.</div>}
                <div className="mt-5">
                  <h3 className="text-sm font-semibold mb-2">Como configurar no cliente SIP</h3>
                  <ol className="list-decimal ml-5 space-y-1.5 text-sm text-[color:var(--text-secondary)]">
                    <li>Crie uma conta SIP e mantenha o registro ativado.</li>
                    <li>Informe o Usuario SIP tambem no campo Usuario de autenticacao ou Auth ID.</li>
                    <li>Use o Dominio como Realm e o Servidor como Proxy ou Registrar.</li>
                    <li>Selecione {config.transport}, porta {config.port}, habilite rport/NAT e keep-alive de 15 a 30 segundos.</li>
                    <li>Deixe em AUTO para negociacao automatica ou informe uma ordem manual de codecs quando precisar fixar um perfil especifico.</li>
                  </ol>
                </div>
              </>;
            })()}
            <div className="flex justify-end mt-5"><button type="button" className="sbc-btn sbc-btn-primary" onClick={() => setSetupInfo(null)}>Concluir</button></div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
